import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { RecoilState } from 'recoil';
import { useSetRecoilState } from 'recoil';

interface UseFrozenDropOptions {
  /**
   * True while the drop's API call + refetch are in flight. Pass the combined
   * loading state (e.g. `isLoading || isLoadingUnassignAppointment`).
   */
  isLoading: boolean;
  /**
   * When true, keep the freeze held even after loading settles because the
   * action isn't finished yet (e.g. a warning dialog is open awaiting the
   * user's decision). Defaults to false. The follow-up call's loading cycle —
   * or an explicit `release()` on cancel — clears the freeze afterwards.
   */
  holdWhileOpen?: boolean;
  /**
   * Optional extra cleanup run on every release (e.g. removing a
   * `data-drag-active` attribute).
   */
  onRelease?: () => void;
}

interface UseFrozenDropReturn<T> {
  /** Sets the frozen-drop atom and marks the freeze active. Call on drop. */
  freeze: (value: T) => void;
  /** Clears the frozen frame immediately. */
  release: () => void;
  /**
   * Stable ref to the latest `release`, so effects/handlers can call it without
   * taking it as a dependency.
   */
  releaseRef: RefObject<() => void>;
}

/**
 * Shared lifecycle for the "frozen drop" preview used by the Pragmatic
 * Timeline / Cards / Roster views: after a drop a copy of the card is held at
 * its target position (and the source card stays faded) until the plan/unassign
 * call + refetch settle, replacing the vanished native drag ghost.
 *
 * The freeze is released the moment the data settles — once loading has been
 * true (request in flight) and turns false (refetch done) — so the card lands
 * at its final spot at full opacity while the preview clears simultaneously.
 * Presentation only; no source-of-truth change.
 */
export function useFrozenDrop<T>(
  atom: RecoilState<T | null>,
  { isLoading, holdWhileOpen = false, onRelease }: UseFrozenDropOptions
): UseFrozenDropReturn<T> {
  const setFrozenDrop = useSetRecoilState(atom);

  const frozenActiveRef = useRef(false);
  const sawFreezeLoadingRef = useRef(false);

  const holdWhileOpenRef = useRef(holdWhileOpen);
  holdWhileOpenRef.current = holdWhileOpen;

  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;

  const release = useCallback(() => {
    frozenActiveRef.current = false;
    sawFreezeLoadingRef.current = false;
    setFrozenDrop(null);
    onReleaseRef.current?.();
  }, [setFrozenDrop]);

  // Stable ref so effects can call the latest release without a dependency.
  const releaseRef = useRef(release);
  releaseRef.current = release;

  const freeze = useCallback(
    (value: T) => {
      frozenActiveRef.current = true;
      sawFreezeLoadingRef.current = false;
      setFrozenDrop(value);
    },
    [setFrozenDrop]
  );

  // Release on unmount so the global atom can't flash a stale preview on remount.
  useEffect(() => {
    return () => {
      releaseRef.current();
    };
  }, []);

  // Release once the data settles: loading was true (request in flight) and
  // turned false (refetch done). While `holdWhileOpen` is true the action isn't
  // finished yet — keep the freeze until a later loading cycle or explicit
  // release clears it.
  useEffect(() => {
    if (!frozenActiveRef.current) return;
    if (isLoading) {
      sawFreezeLoadingRef.current = true;
    } else if (sawFreezeLoadingRef.current) {
      if (holdWhileOpenRef.current) return;
      releaseRef.current();
    }
  }, [isLoading]);

  return { freeze, release, releaseRef };
}
